import { LocaleLink } from "@/components/site/LocaleLink";
import { ArrowUpRight, FileText } from "lucide-react";
import { REPORTS, type Report } from "@/content/reports";
import { REPORT_DETAILS } from "@/content/report-details";
import { REPORT_DETAILS_BG } from "@/content/report-details-bg";
import { useT } from "@/lib/i18n/I18nProvider";

/** Industry/service page each report links to, with descriptive anchor text. */
const TOPIC_LINK: Record<string, { to: string; en: string; bg: string }> = {
  Pharma: { to: "/industries/pharma", en: "Explore media monitoring and analysis for pharma", bg: "Разгледайте медийния мониторинг и анализ за фармацията" },
  Finance: { to: "/industries/fintech", en: "Explore media monitoring and analysis for banking and fintech", bg: "Разгледайте медийния мониторинг и анализ за банки и финтех" },
  Banking: { to: "/industries/fintech", en: "Explore media monitoring and analysis for banking and fintech", bg: "Разгледайте медийния мониторинг и анализ за банки и финтех" },
  Retail: { to: "/industries/retail", en: "Explore media monitoring and analysis for retail", bg: "Разгледайте медийния мониторинг и анализ за търговията на дребно" },
  FMCG: { to: "/industries/fmcg", en: "Explore media monitoring and analysis for FMCG brands", bg: "Разгледайте медийния мониторинг и анализ за FMCG брандове" },
  Energy: { to: "/media-analysis/market-research", en: "See how media-based market research tracks energy trends", bg: "Вижте как пазарните проучвания чрез медиен анализ следят енергийните тенденции" },
  "Defence & Security": { to: "/media-analysis", en: "Learn how media analysis maps narratives across Southeast Europe", bg: "Научете как медийният анализ проследява наративите в Югоизточна Европа" },
  Technology: { to: "/media-analysis/reputation", en: "Learn how reputation analysis measures public sentiment", bg: "Научете как анализът на репутацията измерва общественото мнение" },
  Business: { to: "/media-analysis/competitive-intelligence", en: "See how competitive intelligence benchmarks media visibility", bg: "Вижте как конкурентното разузнаване сравнява медийната видимост" },
};

/** Per-report overrides of the topic link. */
const SLUG_TOPIC: Record<string, string> = { "back-to-school-2026": "FMCG" };

export function relatedLinkFor(report: Report) {
  return TOPIC_LINK[SLUG_TOPIC[report.slug] ?? report.topic ?? ""] ?? TOPIC_LINK["Defence & Security"];
}

/** REPORTS is curated newest-first, so array index is the recency rank. */
const REPORT_RECENCY = new Map(REPORTS.map((r, i) => [r.slug as string, i]));

/** Sort any list newest-first; unknown slugs keep a stable position at the end. */
export function sortByRecency<T>(items: T[], slugOf: (item: T) => string | undefined): T[] {
  return items
    .map((item, i) => ({ item, i, rec: REPORT_RECENCY.get(slugOf(item) ?? "") }))
    .sort((a, b) => (a.rec ?? Infinity) - (b.rec ?? Infinity) || a.i - b.i)
    .map((x) => x.item);
}

/** Most recent reports first, excluding the report being viewed. */
export function relatedReportsFor(report: Report, count = 3): Report[] {
  return sortByRecency(REPORTS.filter((r) => r.slug !== report.slug), (r) => r.slug).slice(0, count);
}

export function RelatedReports({
  slugs,
  reports,
  industryLink,
  className = "border-t border-border/60 bg-secondary/30 py-16 lg:py-20",
}: {
  slugs?: string[];
  reports?: Report[];
  industryLink?: { to: string; en: string; bg: string };
  className?: string;
}) {
  const { locale } = useT();
  const isBg = locale === "bg";
  const raw = reports ?? (slugs ?? []).map((s) => REPORTS.find((r) => r.slug === s)).filter((r): r is Report => !!r);
  const list = sortByRecency(raw, (r) => r.slug);
  if (list.length === 0) return null;
  return (
    <section className={className} aria-labelledby="related-reports-heading">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <h2 id="related-reports-heading" className="font-display text-[clamp(1.4rem,2.2vw,1.75rem)] font-medium text-foreground">
          {isBg ? "Свързани доклади" : "Related reports"}
        </h2>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((r) => (
            <li key={r.slug}>
              <LocaleLink
                to="/reports/$slug"
                params={{ slug: r.slug }}
                className="group flex h-full flex-col justify-between rounded-2xl border border-border/60 bg-background p-5 transition-all hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/10 text-accent">
                    <FileText className="h-4 w-4" aria-hidden />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-accent" aria-hidden />
                </div>
                <h3 className="font-display mt-4 text-[15px] font-medium leading-snug text-foreground">
                  {isBg ? r.titleBg ?? r.title : r.title}
                </h3>
              </LocaleLink>
            </li>
          ))}
        </ul>
        {industryLink && (
          <p className="mt-8 text-[15px]">
            <LocaleLink to={industryLink.to} className="font-medium text-accent underline underline-offset-4 hover:opacity-80">
              {isBg ? industryLink.bg : industryLink.en}
            </LocaleLink>
          </p>
        )}
      </div>
    </section>
  );
}

/** Compact "Up next" shelf for report detail pages: 2 cover cards + browse link. */
export function UpNextReports({ reports, industryLink }: { reports: Report[]; industryLink?: { to: string; en: string; bg: string } }) {
  const { locale } = useT();
  const isBg = locale === "bg";
  if (reports.length === 0) return null;
  return (
    <section className="border-t border-border/60 bg-secondary/30 py-10 lg:py-12" aria-labelledby="up-next-heading">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="up-next-heading" className="font-display text-[clamp(1.2rem,1.8vw,1.45rem)] font-medium text-foreground">
            {isBg ? "Свързани доклади" : "Related reports"}
          </h2>
          <LocaleLink to="/reports" className="text-[14px] font-medium text-accent-strong underline-offset-4 hover:underline">
            {isBg ? "Всички доклади" : "Browse all reports"} →
          </LocaleLink>
        </div>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {reports.map((r) => {
            const cover = (isBg ? REPORT_DETAILS_BG[r.slug]?.heroImageSrc : undefined) ?? REPORT_DETAILS[r.slug]?.heroImage?.src;
            return (
              <li key={r.slug}>
                <LocaleLink
                  to="/reports/$slug"
                  params={{ slug: r.slug }}
                  className="group flex items-center gap-4 rounded-2xl border border-border/60 bg-background p-3 transition-all hover:border-accent/40 hover:shadow-soft"
                >
                  {cover ? (
                    <img src={cover} alt="" loading="lazy" className="h-14 w-24 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <span className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"><FileText className="h-5 w-5" aria-hidden /></span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12px] text-muted-foreground">{r.year}</span>
                    <span className="font-display mt-0.5 line-clamp-2 block text-[15px] font-medium leading-snug text-foreground">{isBg ? r.titleBg ?? r.title : r.title}</span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-accent" aria-hidden />
                </LocaleLink>
              </li>
            );
          })}
        </ul>
        {industryLink && (
          <p className="mt-5 text-[14px]">
            <LocaleLink to={industryLink.to} className="font-medium text-accent-strong underline underline-offset-4 hover:opacity-80">
              {isBg ? industryLink.bg : industryLink.en}
            </LocaleLink>
          </p>
        )}
      </div>
    </section>
  );
}
